import { type ChangeEvent, type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import type { MultiLang } from '../../config';
import Functions from '../../functions';
import type CosmoDbContext from './CosmoDbContext';
import { type CosmoDbListData, CosmoDbListDataType } from './CosmoDbContext';
import CosmoDbUtils from './CosmoDbUtils';

interface Props {
    lang: MultiLang;
    context: CosmoDbContext;
    list: CosmoDbListData;
    params: URLSearchParams;
}

type SortMethod = 'asc' | 'desc';

const CosmoDbListSortBar = (props: Props) => {
    const { lang, context, list, params } = props;
    const navigate = useNavigate();
    const paramSort = params.get('sort');
    const paramN = params.get('n');
    const [sort, setSort] = useState(paramSort !== null && /^\d+$/.test(paramSort) ? parseInt(paramSort, 10) : 1);
    const [sortMethod, setSortMethod] = useState<SortMethod>(params.get('sort_method') === 'asc' ? 'asc' : 'desc');
    const [n, setN] = useState(paramN !== null && /^\d+$/.test(paramN) ? parseInt(paramN, 10) : 20);
    const [size, setSize] = useState(CosmoDbUtils.getThumbnailSize(params.get('size'), list.thumb_size)?.name ?? '');

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const nParams = new URLSearchParams(params);
        if (list.type === CosmoDbListDataType.Thumbnail) {
            nParams.set('size', size);
        }
        nParams.set('sort', sort.toString());
        nParams.set('sort_method', sortMethod);
        nParams.set('n', n.toString());
        void navigate(context.getListUrl(nParams));
    };

    const components = context.getComponents().filter((value) => value.onoff === 0);
    return (
        <form className="list-sortbar" onSubmit={handleSubmit}>
            <select
                value={sort}
                onChange={(event: ChangeEvent<HTMLSelectElement>) => setSort(parseInt(event.target.value, 10))}
            >
                {components.map((value) => (
                    <option key={value.comp_id} value={value.comp_id}>
                        {Functions.mlang(value.tag, lang)}
                    </option>
                ))}
            </select>
            <select
                value={sortMethod}
                onChange={(event: ChangeEvent<HTMLSelectElement>) => setSortMethod(event.target.value as SortMethod)}
            >
                <option value="asc">{Functions.mlang('[en]ascending order[/en][ja]昇順[/ja]', lang)}</option>
                <option value="desc">{Functions.mlang('[en]descending order[/en][ja]降順[/ja]', lang)}</option>
            </select>
            <select
                value={n}
                onChange={(event: ChangeEvent<HTMLSelectElement>) => setN(parseInt(event.target.value, 10))}
            >
                <option value="20">20</option>
                <option value="40">40</option>
                <option value="60">60</option>
                <option value="80">80</option>
                <option value="100">100</option>
            </select>
            {list.type === CosmoDbListDataType.Thumbnail && (
                <select value={size} onChange={(event: ChangeEvent<HTMLSelectElement>) => setSize(event.target.value)}>
                    {list.thumb_size.split(';').map((value) => {
                        const key = value.split(',')[0];
                        return <option key={key}>{key}</option>;
                    })}
                </select>
            )}
            <input className="formButton" type="submit" value="Sort" />
        </form>
    );
};

export default CosmoDbListSortBar;
